import { DndContext, PointerSensor, TouchSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Eye, EyeOff, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

// apply a user's saved menu layout: section order, item order within each section, hidden items
export function applyNavLayout(nav, layout) {
  const L = layout || {};
  const rank = (list, key) => { const i = (list || []).indexOf(key); return i < 0 ? 1e6 : i; };
  return nav
    .map((g, gi) => ({ ...g, gi, items: g.items.map((it, ii) => ({ ...it, ii })).sort((a, b) => rank(L.items?.[g.title], a.path) - rank(L.items?.[g.title], b.path) || a.ii - b.ii) }))
    .sort((a, b) => rank(L.order, a.title) - rank(L.order, b.title) || a.gi - b.gi);
}

function useDnd() {
  return useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
}

function Sortable({ id, children, className }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className={cn(className, isDragging && "relative z-10 opacity-80 shadow-md")}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
}

function ItemRow({ item, hidden, onToggle }) {
  const tid = item.name.toLowerCase().replace(/ /g, "-");
  return (
    <Sortable id={item.path} className="rounded-lg bg-slate-50">
      {(handle) => (
        <div className={cn("flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium", hidden ? "text-slate-300" : "text-slate-600")} data-testid={`nav-edit-item-${tid}`}>
          <button type="button" {...handle} className="cursor-grab touch-none text-slate-300 hover:text-slate-500" aria-label={`Drag ${item.name}`} data-testid={`nav-drag-${tid}`}><GripVertical className="h-4 w-4" /></button>
          <item.icon className="h-[18px] w-[18px] text-slate-400" />
          <span className={cn("flex-1 truncate", hidden && "line-through")}>{item.name}</span>
          <button type="button" onClick={onToggle} className="rounded p-1 text-slate-400 hover:bg-white hover:text-slate-700" title={hidden ? "Show in menu" : "Hide from menu"} data-testid={`nav-toggle-${tid}`}>
            {hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      )}
    </Sortable>
  );
}

function SectionBlock({ group, hidden, onToggle, onItems }) {
  const sensors = useDnd();
  const ids = group.items.map((i) => i.path);
  const end = ({ active, over }) => { if (over && active.id !== over.id) onItems(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id))); };
  return (
    <Sortable id={`sec:${group.title}`} className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-1.5">
      {(handle) => (
        <>
          <div className="mb-1 flex items-center gap-1.5 px-1.5" data-testid={`nav-edit-section-${group.title.toLowerCase()}`}>
            <button type="button" {...handle} className="cursor-grab touch-none text-slate-300 hover:text-slate-500" aria-label={`Drag ${group.title}`} data-testid={`nav-drag-section-${group.title.toLowerCase()}`}><GripVertical className="h-4 w-4" /></button>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{group.title}</p>
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={end}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              <div className="space-y-px">{group.items.map((it) => <ItemRow key={it.path} item={it} hidden={hidden.includes(it.path)} onToggle={() => onToggle(it.path)} />)}</div>
            </SortableContext>
          </DndContext>
        </>
      )}
    </Sortable>
  );
}

// edit mode: drag sections, drag items within their section, show/hide items
export function NavEditor({ nav, layout, onChange }) {
  const sensors = useDnd();
  const hidden = layout.hidden || [];
  const titles = nav.map((g) => g.title);
  const secIds = titles.map((t) => `sec:${t}`);
  const endSec = ({ active, over }) => {
    if (over && active.id !== over.id) onChange({ ...layout, order: arrayMove(titles, secIds.indexOf(active.id), secIds.indexOf(over.id)) });
  };
  const toggle = (p) => onChange({ ...layout, hidden: hidden.includes(p) ? hidden.filter((x) => x !== p) : [...hidden, p] });
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={endSec}>
      <SortableContext items={secIds} strategy={verticalListSortingStrategy}>
        <div className="select-none space-y-2" data-testid="nav-editor">
          {nav.map((g) => <SectionBlock key={g.title} group={g} hidden={hidden} onToggle={toggle} onItems={(ids) => onChange({ ...layout, items: { ...(layout.items || {}), [g.title]: ids } })} />)}
        </div>
      </SortableContext>
    </DndContext>
  );
}
